import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Paper,
  Stack,
  Divider,
  Grid,
  Skeleton,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Timeline as EvolutionIcon,
  TrendingUp as GrowthIcon,
  AddCircle as AddedIcon,
  Topic as TopicIcon,
  Speed as RateIcon,
  Description as DocumentIcon,
  Chat as MessageIcon,
  Hub as ConceptIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import ModeSelector from '../../components/Navigation/ModeSelector';
import TemporalSlider from '../../components/KnowledgeGraph/TemporalSlider/TemporalSlider';
import { graphApi } from '../../services/api/graph.service';
import { GraphNode, TemporalMetadata, NodeType } from '../../types/graph.types';

/**
 * EvolutionPage
 *
 * Temporal knowledge analysis — shows how the user's knowledge
 * grew over time using a date-range slider, growth metrics,
 * most active topics, and an activity timeline.
 *
 * Reuses the existing TemporalSlider component and temporal API.
 */

interface EvolutionState {
  nodes: GraphNode[];
  temporalMetadata: TemporalMetadata | null;
  loading: boolean;
  error: string | null;
}

const EvolutionPage: React.FC = () => {
  const [state, setState] = useState<EvolutionState>({
    nodes: [],
    temporalMetadata: null,
    loading: true,
    error: null,
  });

  // Full date range (for the slider bounds) — set once on initial load
  const [fullRange, setFullRange] = useState<{ min: Date; max: Date }>({
    min: new Date(Date.now() - 365 * 86400000), // 1 year ago
    max: new Date(),
  });
  const [, setFullRangeLoaded] = useState(false);

  // Selected date range
  const [startDate, setStartDate] = useState<Date>(
    new Date(Date.now() - 90 * 86400000) // Default: last 90 days
  );
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [granularity, setGranularity] = useState<'day' | 'week' | 'month'>('week');

  /**
   * Fetch temporal data from the API
   */
  const fetchTemporalData = useCallback(async (start: Date, end: Date, gran: 'day' | 'week' | 'month') => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await graphApi.getTemporalData(
        'personal',
        start.toISOString(),
        end.toISOString(),
        gran
      );

      const { nodes, temporalMetadata } = response.data;

      setState({
        nodes,
        temporalMetadata,
        loading: false,
        error: null,
      });
    } catch (err: any) {
      console.error('Error fetching temporal data:', err);
      setState(prev => ({
        ...prev,
        loading: false,
        error: err.response?.data?.error || err.message || 'Failed to load temporal data',
      }));
    }
  }, []);

  // Initial load: fetch with wide range to discover true data bounds, then fetch selected range
  useEffect(() => {
    const init = async () => {
      try {
        // First, fetch with a very wide range to discover the true data bounds
        const wideStart = new Date(Date.now() - 2 * 365 * 86400000); // 2 years back
        const wideEnd = new Date();
        const response = await graphApi.getTemporalData(
          'personal',
          wideStart.toISOString(),
          wideEnd.toISOString(),
          'month'
        );

        const { temporalMetadata } = response.data;
        if (temporalMetadata?.oldestDate && temporalMetadata?.newestDate) {
          setFullRange({
            min: new Date(temporalMetadata.oldestDate),
            max: new Date(temporalMetadata.newestDate),
          });
        }
        setFullRangeLoaded(true);

        // Then fetch with the default selected range
        await fetchTemporalData(startDate, endDate, granularity);
      } catch {
        // If wide range fetch fails, just fetch the default range
        setFullRangeLoaded(true);
        await fetchTemporalData(startDate, endDate, granularity);
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handle slider date range change
   */
  const handleDateRangeChange = useCallback((newStart: Date, newEnd: Date) => {
    setStartDate(newStart);
    setEndDate(newEnd);
    fetchTemporalData(newStart, newEnd, granularity);
  }, [granularity, fetchTemporalData]);

  /**
   * Handle granularity change
   */
  const handleGranularityChange = useCallback((newGranularity: 'day' | 'week' | 'month') => {
    setGranularity(newGranularity);
    fetchTemporalData(startDate, endDate, newGranularity);
  }, [startDate, endDate, fetchTemporalData]);

  /**
   * Compute growth metrics from the nodes
   */
  const metrics = useMemo(() => {
    const { nodes } = state;
    const totalNodes = nodes.length;

    // Count by type
    const typeCounts: Record<string, number> = {};
    nodes.forEach(node => {
      const t = node.type || 'unknown';
      typeCounts[t] = (typeCounts[t] || 0) + 1;
    });

    // Count topics
    const topicCount = typeCounts[NodeType.TOPIC] || 0;

    // Compute growth rate (nodes per day in the selected range)
    const daySpan = Math.max(1, (endDate.getTime() - startDate.getTime()) / 86400000);
    const growthRate = totalNodes / daySpan;

    return { totalNodes, topicCount, growthRate, typeCounts };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.nodes, startDate, endDate]);

  /**
   * Compute most active topics
   */
  const activeTopics = useMemo(() => {
    const topicNodes = state.nodes.filter(n => n.type === NodeType.TOPIC);
    return topicNodes
      .sort((a, b) => {
        const freqA = (a as any).properties?.frequency || 0;
        const freqB = (b as any).properties?.frequency || 0;
        return freqB - freqA;
      })
      .slice(0, 10);
  }, [state.nodes]);

  /**
   * Compute activity timeline (most recent nodes)
   */
  const activityTimeline = useMemo(() => {
    return [...state.nodes]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 15);
  }, [state.nodes]);

  /**
   * Get icon for a node type
   */
  const getNodeIcon = (type: string) => {
    switch (type) {
      case NodeType.DOCUMENT: return <DocumentIcon fontSize="small" style={{ color: '#325BA8' }} />;
      case NodeType.MESSAGE: return <MessageIcon fontSize="small" style={{ color: '#999' }} />;
      case NodeType.TOPIC: return <TopicIcon fontSize="small" style={{ color: '#4DC4D2' }} />;
      case NodeType.CONCEPT: return <ConceptIcon fontSize="small" style={{ color: '#892987' }} />;
      case NodeType.PERSON: return <PersonIcon fontSize="small" style={{ color: '#E94257' }} />;
      default: return <ConceptIcon fontSize="small" style={{ color: '#999' }} />;
    }
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Header */}
      <Paper
        elevation={0}
        style={{
          paddingLeft: 24,
          paddingRight: 24,
          paddingTop: 12,
          paddingBottom: 12,
          borderBottom: '1px solid rgba(0,0,0,0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <ModeSelector size="small" showLabels />
          <Divider orientation="vertical" flexItem />
          <Stack direction="row" spacing={1} alignItems="center">
            <EvolutionIcon style={{ color: '#4DC4D2' }} />
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Evolution Mode
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {/* Content */}
      <Container maxWidth="lg" style={{ flex: 1, paddingTop: 24, paddingBottom: 24, overflow: 'auto' }}>
        {/* Error */}
        {state.error && (
          <Alert severity="error" style={{ marginBottom: 24 }} onClose={() => setState(prev => ({ ...prev, error: null }))}>
            {state.error}
          </Alert>
        )}

        {/* Temporal Slider */}
        <Paper elevation={0} style={{ padding: 24, marginBottom: 24, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8 }}>
          <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 16 }}>
            Knowledge Timeline
          </Typography>
          <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)', marginBottom: 16 }}>
            Select a date range to see how your knowledge evolved during that period.
          </Typography>
          {state.temporalMetadata ? (
            <TemporalSlider
              minDate={fullRange.min}
              maxDate={fullRange.max}
              startDate={startDate}
              endDate={endDate}
              granularity={granularity}
              distribution={state.temporalMetadata.distribution}
              onChange={handleDateRangeChange}
              onGranularityChange={handleGranularityChange}
            />
          ) : state.loading ? (
            <Skeleton variant="rectangular" height={80} style={{ borderRadius: 4 }} />
          ) : (
            <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)' }}>
              No temporal data available. Start building your knowledge graph.
            </Typography>
          )}
        </Paper>

        {/* Growth Metrics */}
        {state.loading ? (
          <Grid container spacing={2} style={{ marginBottom: 24 }}>
            {[1, 2, 3, 4].map(i => (
              <Grid item xs={6} md={3} key={i}>
                <Skeleton variant="rectangular" height={100} style={{ borderRadius: 4 }} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={2} style={{ marginBottom: 24 }}>
            <Grid item xs={6} md={3}>
              <Paper elevation={0} style={{ padding: 16, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8, textAlign: 'center' }}>
                <GrowthIcon style={{ fontSize: 28, color: '#4DC4D2', marginBottom: 4 }} />
                <Typography variant="h4" style={{ fontWeight: 700, color: '#4DC4D2' }}>
                  {metrics.totalNodes}
                </Typography>
                <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)' }}>
                  Total Nodes
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper elevation={0} style={{ padding: 16, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8, textAlign: 'center' }}>
                <AddedIcon style={{ fontSize: 28, color: '#325BA8', marginBottom: 4 }} />
                <Typography variant="h4" style={{ fontWeight: 700, color: '#325BA8' }}>
                  {metrics.typeCounts[NodeType.DOCUMENT] || 0}
                </Typography>
                <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)' }}>
                  Documents
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper elevation={0} style={{ padding: 16, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8, textAlign: 'center' }}>
                <TopicIcon style={{ fontSize: 28, color: '#892987', marginBottom: 4 }} />
                <Typography variant="h4" style={{ fontWeight: 700, color: '#892987' }}>
                  {metrics.topicCount}
                </Typography>
                <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)' }}>
                  Topics
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper elevation={0} style={{ padding: 16, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8, textAlign: 'center' }}>
                <RateIcon style={{ fontSize: 28, color: '#E94257', marginBottom: 4 }} />
                <Typography variant="h4" style={{ fontWeight: 700, color: '#E94257' }}>
                  {metrics.growthRate.toFixed(1)}
                </Typography>
                <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)' }}>
                  Nodes / Day
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Two-Column: Active Topics + Activity Timeline */}
        {!state.loading && state.nodes.length > 0 && (
          <Grid container spacing={3}>
            {/* Most Active Topics */}
            <Grid item xs={12} md={5}>
              <Paper elevation={0} style={{ padding: 16, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8 }}>
                <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8, color: '#4DC4D2' }}>
                  Most Active Topics
                </Typography>
                {activeTopics.length === 0 ? (
                  <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)' }}>
                    No topics found in the selected range.
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {activeTopics.map((topic, idx) => (
                      <ListItem key={topic.id} disableGutters style={{ paddingTop: 4, paddingBottom: 4 }}>
                        <ListItemIcon style={{ minWidth: 32 }}>
                          <Typography variant="body2" style={{ fontWeight: 600, color: 'rgba(0,0,0,0.6)' }}>
                            {idx + 1}.
                          </Typography>
                        </ListItemIcon>
                        <ListItemText
                          primary={topic.label}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        />
                        <Chip
                          label={`${(topic as any).properties?.frequency || 0}`}
                          size="small"
                          style={{
                            height: 20,
                            fontSize: '0.7rem',
                            backgroundColor: 'rgba(77,196,210,0.15)',
                            color: '#4DC4D2',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>

            {/* Activity Timeline */}
            <Grid item xs={12} md={7}>
              <Paper elevation={0} style={{ padding: 16, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8 }}>
                <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8, color: '#325BA8' }}>
                  Recent Activity
                </Typography>
                {activityTimeline.length === 0 ? (
                  <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)' }}>
                    No activity in the selected range.
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {activityTimeline.map((node) => (
                      <ListItem key={node.id} disableGutters style={{ paddingTop: 4, paddingBottom: 4, alignItems: 'flex-start' }}>
                        <ListItemIcon style={{ minWidth: 32, marginTop: 4 }}>
                          {getNodeIcon(node.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={node.label}
                          secondary={formatDate(node.createdAt)}
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontWeight: 500,
                            noWrap: true,
                          }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <Chip
                          label={node.type}
                          size="small"
                          style={{
                            height: 20,
                            fontSize: '0.65rem',
                            marginLeft: 8,
                            flexShrink: 0,
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Empty state */}
        {!state.loading && state.nodes.length === 0 && !state.error && (
          <div style={{ textAlign: 'center', paddingTop: 64, paddingBottom: 64 }}>
            <EvolutionIcon style={{ fontSize: 64, color: 'rgba(0,0,0,0.26)', marginBottom: 16 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No knowledge data in the selected range
            </Typography>
            <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)', maxWidth: 480, margin: '0 auto' }}>
              Try expanding the date range or start building your knowledge graph
              by chatting with your AI teammates and uploading documents.
            </Typography>
          </div>
        )}
      </Container>
    </div>
  );
};

export default EvolutionPage;
